/**
 * The six world story guides (curriculum v2 storyBeat cards, map chapter signs, finale): simple
 * Storybook-style SVG characters drawn for Sketchling (warm ink outline, flat fills, one blush).
 * scribble = the existing Scribble mascot; miso (orange kitten), hatDe (red squirrel carrying an acorn), bolt (toy robot),
 * nova (little night star), banhBao (Bé Bun / Bunbun, steamed bun in a chef's hat).
 */
import { memo, type ReactElement } from 'react';
import type { StoryGuide as GuideId } from '@sketchling/core';
import { Scribble } from './characters';

const INK = '#4A3226';
const BLUSH = '#F4A3A0';

function Face({ cx, cy, s = 1, happy = true }: { cx: number; cy: number; s?: number; happy?: boolean }) {
  return (
    <g>
      <ellipse cx={cx - 9 * s} cy={cy} rx={2.6 * s} ry={3.4 * s} fill={INK} />
      <ellipse cx={cx + 9 * s} cy={cy} rx={2.6 * s} ry={3.4 * s} fill={INK} />
      <circle cx={cx - 8.2 * s} cy={cy - 1.2 * s} r={0.9 * s} fill="#fff" />
      <circle cx={cx + 9.8 * s} cy={cy - 1.2 * s} r={0.9 * s} fill="#fff" />
      <ellipse cx={cx - 15 * s} cy={cy + 6 * s} rx={4 * s} ry={2.4 * s} fill={BLUSH} opacity={0.8} />
      <ellipse cx={cx + 15 * s} cy={cy + 6 * s} rx={4 * s} ry={2.4 * s} fill={BLUSH} opacity={0.8} />
      <path d={happy ? `M${cx - 5 * s} ${cy + 6 * s}Q${cx} ${cy + 11 * s} ${cx + 5 * s} ${cy + 6 * s}` : `M${cx - 4 * s} ${cy + 8 * s}L${cx + 4 * s} ${cy + 8 * s}`} fill="none" stroke={INK} strokeWidth={2.2 * s} strokeLinecap="round" />
    </g>
  );
}

function Miso() {
  return (
    <g strokeLinejoin="round">
      <path d="M22 44L28 14L46 32Z" fill="#F2A65A" stroke={INK} strokeWidth="3" />
      <path d="M78 44L72 14L54 32Z" fill="#F2A65A" stroke={INK} strokeWidth="3" />
      <path d="M28 40L30 22L40 32Z" fill="#F7C8B5" />
      <path d="M72 40L70 22L60 32Z" fill="#F7C8B5" />
      <ellipse cx="50" cy="56" rx="34" ry="30" fill="#F2A65A" stroke={INK} strokeWidth="3" />
      <path d="M40 30Q44 38 42 44M50 28V40M60 30Q56 38 58 44" fill="none" stroke="#D9803A" strokeWidth="3" strokeLinecap="round" />
      <ellipse cx="50" cy="68" rx="15" ry="11" fill="#FBE3CC" />
      <Face cx={50} cy={56} />
      <path d="M47.5 62L50 64.5L52.5 62Z" fill={INK} />
      <path d="M20 60L32 62M20 68L32 66M80 60L68 62M80 68L68 66" stroke={INK} strokeWidth="1.8" strokeLinecap="round" />
    </g>
  );
}

/** Hạt Dẻ (Chestnut): a little red squirrel who always carries an acorn (bushy tail, tufted ears, acorn in his paws). */
function HatDe() {
  return (
    <g strokeLinejoin="round">
      {/* bushy tail curling up behind */}
      <path d="M64 86Q96 84 94 52Q92 26 74 20Q60 18 62 32Q76 30 80 46Q84 66 62 70Z" fill="#C8693A" stroke={INK} strokeWidth="3" />
      <path d="M72 30Q84 38 84 54M68 40Q76 48 74 62" fill="none" stroke="#E79A62" strokeWidth="2.4" strokeLinecap="round" />
      {/* ears with tufts */}
      <path d="M26 34L24 12L40 26Z" fill="#C8693A" stroke={INK} strokeWidth="3" />
      <path d="M62 30L66 10L50 24Z" fill="#C8693A" stroke={INK} strokeWidth="3" />
      <path d="M28 30L27 18L35 26ZM60 28L62 17L54 25Z" fill="#F4C7A6" />
      {/* body + head */}
      <ellipse cx="44" cy="76" rx="22" ry="16" fill="#C8693A" stroke={INK} strokeWidth="3" />
      <ellipse cx="44" cy="46" rx="26" ry="22" fill="#D27744" stroke={INK} strokeWidth="3" />
      <ellipse cx="44" cy="56" rx="15" ry="10" fill="#FBE3CC" />
      <Face cx={44} cy={46} s={0.85} />
      <ellipse cx="44" cy="51.5" rx="2.4" ry="1.8" fill={INK} />
      {/* the acorn he carries, held in two paws */}
      <path d="M36 74Q36 90 46 92Q56 90 56 74Z" fill="#E2B27A" stroke={INK} strokeWidth="2.6" />
      <path d="M33 74Q34 64 46 64Q58 64 59 74Q46 78 33 74Z" fill="#8C5A3C" stroke={INK} strokeWidth="2.6" />
      <path d="M38 70L44 66M44 72L52 66M50 73L56 69" stroke="#6E4329" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M46 64Q46 58 50 56" fill="none" stroke={INK} strokeWidth="2.4" strokeLinecap="round" />
      <ellipse cx="34" cy="80" rx="5" ry="4" fill="#D27744" stroke={INK} strokeWidth="2.4" />
      <ellipse cx="58" cy="80" rx="5" ry="4" fill="#D27744" stroke={INK} strokeWidth="2.4" />
    </g>
  );
}

function Bolt() {
  return (
    <g strokeLinejoin="round">
      <path d="M50 20V10" stroke={INK} strokeWidth="3" strokeLinecap="round" />
      <circle cx="50" cy="9" r="5" fill="#FFC23A" stroke={INK} strokeWidth="2.5" />
      <rect x="16" y="20" width="68" height="58" rx="16" fill="#9FC7E0" stroke={INK} strokeWidth="3" />
      <rect x="26" y="32" width="48" height="34" rx="10" fill="#1F4E63" stroke={INK} strokeWidth="2.5" />
      <ellipse cx="41" cy="46" rx="4" ry="5" fill="#7DF0C2" />
      <ellipse cx="59" cy="46" rx="4" ry="5" fill="#7DF0C2" />
      <path d="M43 56Q50 61 57 56" fill="none" stroke="#7DF0C2" strokeWidth="2.6" strokeLinecap="round" />
      <rect x="8" y="40" width="8" height="18" rx="4" fill="#E9783A" stroke={INK} strokeWidth="2.5" />
      <rect x="84" y="40" width="8" height="18" rx="4" fill="#E9783A" stroke={INK} strokeWidth="2.5" />
      <rect x="34" y="78" width="32" height="12" rx="5" fill="#9FC7E0" stroke={INK} strokeWidth="3" />
    </g>
  );
}

function Nova() {
  const star = 'M50 8L61 36L92 38L68 57L76 88L50 71L24 88L32 57L8 38L39 36Z';
  return (
    <g strokeLinejoin="round">
      <path d={star} fill="#FFD45E" stroke={INK} strokeWidth="3" />
      <path d="M50 18L57 37L44 37Z" fill="#FFF1C1" opacity="0.8" />
      <Face cx={50} cy={52} s={0.9} />
      <path d="M86 12l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="#B98BFF" />
      <path d="M12 74l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z" fill="#5AD1FF" />
    </g>
  );
}

/** Bé Bun / Bunbun (id `banhBao`): a little steamed bun in a tiny chef's hat (ART-BIBLE). */
function BanhBao() {
  return (
    <g strokeLinejoin="round">
      <ellipse cx="50" cy="86" rx="34" ry="6" fill="#000" opacity="0.08" />
      <path d="M12 78Q10 40 50 32Q90 40 88 78Q50 88 12 78Z" fill="#FFF7EA" stroke={INK} strokeWidth="3" />
      <path d="M30 46Q36 52 34 60M70 46Q64 52 66 60" fill="none" stroke="#EAD8BF" strokeWidth="2.4" strokeLinecap="round" />
      {/* tiny chef's hat, a little tilted */}
      <g transform="rotate(-8 50 22)">
        <rect x="38" y="24" width="24" height="9" rx="2" fill="#FFFFFF" stroke={INK} strokeWidth="2.6" />
        <path d="M38 26Q30 22 33 14Q36 7 44 10Q47 3 54 6Q62 4 64 12Q72 14 66 22Q64 26 62 26Z" fill="#FFFFFF" stroke={INK} strokeWidth="2.6" />
      </g>
      <Face cx={50} cy={62} />
    </g>
  );
}

const ART: Record<Exclude<GuideId, 'scribble'>, () => ReactElement> = { miso: Miso, hatDe: HatDe, bolt: Bolt, nova: Nova, banhBao: BanhBao };

export const StoryGuideArt = memo(function StoryGuideArt({ who, size = 96, className = '' }: { who: GuideId; size?: number; className?: string }) {
  if (who === 'scribble') return <Scribble mood="happy" size={size} className={className} />;
  const A = ART[who] ?? Miso;
  return (
    <svg className={`story-guide v2sg-${who} ${className}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden data-guide={who}>
      <A />
    </svg>
  );
});
